import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateBeneficiaryStatusLookupListFormValues } from '../../../../../interfaces/lookup/beneficiaryStatus';
import { IUpdateBeneficiaryStatus } from '../../../../../interfaces/lookup/beneficiaryStatus/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchBeneficiaryStatusById, updateBeneficiaryStatus } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const BeneficiaryStatusLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateBeneficiaryStatusLookupListFormValues) => {
        const body: IUpdateBeneficiaryStatus = {
            id: lookupSlice.detailsPage.beneficiaryStatus.data?.id,
            ...(values as any),
        };

        dispatch(updateBeneficiaryStatus(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.beneficiaryStatus.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/beneficiary-status', { replace: true });
        }
    }, [lookupSlice.detailsPage.beneficiaryStatus.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchBeneficiaryStatusById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.beneficiaryStatus.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.beneficiaryStatus.data.id,
                code: lookupSlice.detailsPage.beneficiaryStatus.data.code,
                description: lookupSlice.detailsPage.beneficiaryStatus.data.description,
            } as IUpdateBeneficiaryStatusLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.beneficiaryStatus.data]);

    if (lookupSlice.detailsPage.beneficiaryStatus.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="beneficiarystatus-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage beneficiary status lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Beneficiary status',
                        path: '/admin/lookup-lists/beneficiary-status',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.beneficiaryStatus.data?.description || '-',
                        path: `/admin/lookup-lists/beneficiary-status/${params.id}`,
                    },
                    {
                        breadcrumbName: 'Edit',
                        path: `/admin/lookup-lists/beneficiary-status/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.beneficiaryStatus.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.beneficiaryStatus.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.beneficiaryStatus.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.beneficiaryStatus.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.beneficiaryStatus.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.beneficiaryStatus.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.beneficiaryStatus.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.beneficiaryStatus.fetchStatus} about={'Beneficiary status'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Beneficiary status Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <p>{lookupSlice.detailsPage.beneficiaryStatus?.data?.code || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <Form.Item name="description" rules={[{ required: true, message: "'Description' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.beneficiaryStatus?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.beneficiaryStatus.data?.created
                                            ? dayjs(lookupSlice.detailsPage.beneficiaryStatus.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.beneficiaryStatus.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.beneficiaryStatus.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.beneficiaryStatus.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
